import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Sprout, ArrowBigUp, Send, Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const GARDEN_VIDEO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/1j0r2ocb_gemini_generated_video_432be3f0.mp4";

const T = {
  fr: { eyebrow: "Prochainement", title: "Le Jardin des idées",
    desc: "Une boîte où les idées de projets germent. Propose la tienne, et fais pousser celles des autres en votant.",
    placeholder: "Une idée qui pourrait germer ici…", submit: "Planter mon idée", posting: "Je plante…",
    login: "Connecte-toi pour proposer et voter", empty: "Sois le premier à planter une idée 🌱",
    voteLogin: "Connecte-toi pour voter", ok: "Ton idée est plantée, merci 🌱", err: "Une erreur est survenue." },
  en: { eyebrow: "Coming soon", title: "The Garden of Ideas",
    desc: "A box where project ideas sprout. Share yours, and help others grow by voting.",
    placeholder: "An idea that could sprout here…", submit: "Plant my idea", posting: "Planting…",
    login: "Log in to suggest and vote", empty: "Be the first to plant an idea 🌱",
    voteLogin: "Log in to vote", ok: "Your idea is planted, thank you 🌱", err: "Something went wrong." },
  es: { eyebrow: "Próximamente", title: "El Jardín de las ideas",
    desc: "Una caja donde germinan las ideas de proyectos. Propón la tuya y haz crecer las de los demás votando.",
    placeholder: "Una idea que podría germinar aquí…", submit: "Plantar mi idea", posting: "Plantando…",
    login: "Inicia sesión para proponer y votar", empty: "Sé el primero en plantar una idea 🌱",
    voteLogin: "Inicia sesión para votar", ok: "Tu idea está plantada, gracias 🌱", err: "Ha ocurrido un error." },
  nl: { eyebrow: "Binnenkort", title: "De Ideeëntuin",
    desc: "Een doos waar projectideeën ontkiemen. Deel de jouwe en laat die van anderen groeien door te stemmen.",
    placeholder: "Een idee dat hier zou kunnen ontkiemen…", submit: "Mijn idee planten", posting: "Planten…",
    login: "Log in om voor te stellen en te stemmen", empty: "Wees de eerste die een idee plant 🌱",
    voteLogin: "Log in om te stemmen", ok: "Je idee is geplant, bedankt 🌱", err: "Er is iets misgegaan." },
};

export function GardenSection() {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const L = T[(i18n.language || "fr").slice(0, 2)] || T.fr;
  const [ideas, setIdeas] = useState([]);
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/garden/ideas");
      setIdeas(data);
    } catch { /* silencieux */ }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const submit = async () => {
    if (!text.trim() || posting) return;
    setPosting(true);
    try {
      const { data } = await api.post("/garden/ideas", { content: text.trim() });
      setIdeas((prev) => [data, ...prev.filter((i) => i.id !== data.id)]);
      setText("");
      toast.success(L.ok);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || L.err);
    } finally { setPosting(false); }
  };

  const vote = async (id) => {
    if (!user) { toast.error(L.voteLogin); return; }
    // optimistic
    setIdeas((prev) => prev.map((i) => i.id === id
      ? { ...i, voted: !i.voted, votes: i.votes + (i.voted ? -1 : 1) } : i));
    try {
      const { data } = await api.post(`/garden/ideas/${id}/vote`);
      setIdeas((prev) => prev.map((i) => i.id === id ? { ...i, voted: data.voted, votes: data.votes } : i));
    } catch {
      load();
    }
  };

  return (
    <section className="max-w-6xl mx-auto px-6 py-14 sm:py-24" data-testid="garden-section">
      <div className="text-center mb-12">
        <p className="inline-flex items-center gap-2 text-primary/80 tracking-[0.25em] uppercase text-xs mb-5">
          <Sprout size={14} /> {L.eyebrow}
        </p>
        <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl tracking-tight mb-5">{L.title}</h2>
        <p className="text-foreground/55 max-w-2xl mx-auto leading-relaxed">{L.desc}</p>
      </div>

      {/* Vidéo (fondu comme le héros) */}
      <div className="max-w-3xl mx-auto mb-12">
        <video
          src={GARDEN_VIDEO}
          autoPlay loop muted playsInline
          preload="none"
          data-testid="garden-video"
          className="w-full h-auto object-cover hero-video-fade"
        />
      </div>

      {/* Dépôt d'idée */}
      <div className="max-w-2xl mx-auto mb-12">
        {user ? (
          <div className="glass frame-pulse rounded-3xl p-5 sm:p-6">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              maxLength={600}
              placeholder={L.placeholder}
              data-testid="garden-input"
              className="w-full bg-transparent outline-none resize-none text-foreground/90 placeholder:text-foreground/35"
            />
            <div className="flex justify-end mt-2">
              <button
                onClick={submit}
                disabled={posting || !text.trim()}
                data-testid="garden-submit"
                className="btn-frost-orange inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm disabled:opacity-40"
              >
                {posting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                {posting ? L.posting : L.submit}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <Link
              to="/entrer"
              data-testid="garden-login-cta"
              className="btn-frost-orange inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold text-base"
            >
              <Lock size={18} /> {L.login}
            </Link>
          </div>
        )}
      </div>

      {/* Mur d'idées */}
      {ideas.length === 0 ? (
        <p className="text-center text-foreground/40" data-testid="garden-empty">{L.empty}</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="garden-wall">
          <AnimatePresence>
            {ideas.map((idea) => (
              <motion.div
                key={idea.id}
                layout
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass rounded-2xl p-5 flex flex-col justify-between gap-4"
                data-testid="garden-idea"
              >
                <p className="text-foreground/85 text-sm leading-relaxed">{idea.content}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-foreground/40">{idea.user_name}</span>
                  <button
                    onClick={() => vote(idea.id)}
                    data-testid={`garden-vote-${idea.id}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${
                      idea.voted ? "border-primary/50 bg-primary/15 text-primary"
                      : "border-white/12 text-foreground/60 hover:border-primary/40 hover:text-primary"}`}
                  >
                    <ArrowBigUp size={16} className={idea.voted ? "fill-current" : ""} />
                    {idea.votes}
                  </button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </section>
  );
}
